import {
  AppBar,
  Avatar,
  Button,
  Divider,
  Link,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { mokedNumber } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop, useIsWebView } from 'Hooks';
import {
  Call,
  FooterBackgroundVectors,
  FooterCall,
  FooterContact,
  FooterVectors,
  Message,
  MobileContact,
  MobileFooterLogo,
  NewLogo,
  YourTeam,
} from 'Images';
import { useNavigate } from 'react-router-dom';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';

export const Footer = () => {
  const isDesktop = useIsDesktop();
  const isWebView = useIsWebView();
  const dictionary = useDictionary(Dictionary, DICTIONARY.Footer);
  const navigate = useNavigate();

  if (isWebView) return null;

  return isDesktop ? (
    <AppBar
      component={'footer'}
      sx={{
        position: 'relative',
        padding: '56px 0px 26px 0px',
        paddingRight: { sm: '10%', xl: '250px' },
        zIndex: 1100,
      }}
    >
      <Stack>
        <Stack
          sx={{
            maxWidth: 1000,
            gap: '56px',
          }}
        >
          <Stack
            sx={{
              gap: '48px',
            }}
          >
            <Stack
              direction={'row'}
              sx={{
                gap: '100px',
              }}
            >
              <Stack
                direction={'row'}
                onClick={() => {
                  window.location.href =
                    import.meta.env.VITE_APP_MARKETING_SITE_URL || '';
                }}
                onKeyDown={onEnterOrEscapeKeyDown}
                tabIndex={0}
                role='link'
                sx={{
                  width: 'auto',
                  gap: '20px',
                  padding: 0,
                  margin: 0,
                  alignItems: 'center',
                  height: 'min-content',
                  ml: '74px',
                  cursor: 'pointer',
                }}
              >
                <Avatar
                  src={NewLogo}
                  alt={''}
                  sx={{
                    height: '46px',
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
              </Stack>
              {Object.values(dictionary.links).map((link, index1) => (
                <Stack
                  key={index1}
                  sx={{
                    gap: '12px',
                    width: 'fit-content',
                  }}
                >
                  <Typography
                    variant={'body1'}
                    component={'h3'}
                    id={`${link.title.toLowerCase().replace(' ', '-')}-link`}
                    sx={{
                      color: '#fff',
                    }}
                  >
                    {link.title}
                  </Typography>
                  <Stack
                    component={'ul'}
                    aria-labelledby={`${link.title
                      .toLowerCase()
                      .replace(' ', '-')}-link`}
                    sx={{
                      width: 'auto',
                      gap: '12px',
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    {link.redirects.map((redirect, index2) => (
                      <ListItem
                        key={index2}
                        sx={{
                          padding: 0,
                          marginBottom: '16px',
                          ':hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        <Link href={redirect.href} underline={'none'}>
                          <Typography
                            variant={'body6'}
                            sx={{
                              color: '#fff',
                            }}
                          >
                            {redirect.label}
                          </Typography>
                        </Link>
                      </ListItem>
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Stack>
          <Divider
            orientation='horizontal'
            aria-hidden={'true'}
            sx={{ borderColor: 'primary.shade_50' }}
          />
          <Stack
            direction={'row'}
            sx={{
              gap: '20px',
              height: '56px',
            }}
          >
            <Button
              variant={'primary'}
              role={'link'}
              href={`tel:${mokedNumber}`}
              sx={{
                width: '120px',
                height: '100%',
                padding: '12px',
                gap: '12px',
              }}
            >
              <Avatar
                alt={''}
                src={Call}
                sx={{ width: '20px', height: '24px' }}
              />
              <Typography variant={'body2'}>{dictionary.callButton}</Typography>
            </Button>
            <Button
              variant={'purple'}
              sx={{
                width: '160px',
                height: '100%',
                padding: '12px',
                gap: '12px',
              }}
              onClick={() => {
                navigate('/personal-team?Contact=1');
              }}
              role='link'
            >
              <Avatar
                alt=''
                src={Message}
                sx={{ width: '20px', height: '20px' }}
              />
              <Typography variant={'body2'}>
                {dictionary.contactButton}
              </Typography>
            </Button>
            <Button
              variant={'secondary'}
              sx={{
                width: '160px',
                height: '100%',
                padding: '12px',
                gap: '12px',
                '&:hover': {
                  bgcolor: '#fff',
                },
                cursor: 'pointer',
              }}
              onClick={() => navigate('/personal-team')}
              role='link'
            >
              <Avatar
                alt=''
                src={YourTeam}
                sx={{ width: '20px', height: '20px' }}
              />
              <Typography color={'primary'} variant={'body2'}>
                {dictionary.yourTeam}
              </Typography>
            </Button>
          </Stack>
        </Stack>
        <Stack
          direction={'row'}
          sx={{
            justifyContent: 'center',
            marginTop: '24px',
            gap: '20px',
          }}
        >
          <Typography variant={'body6'}>{dictionary.rightsSymbol}</Typography>
          <Typography variant={'body6'}>{dictionary.rights}</Typography>
        </Stack>
      </Stack>
      <Avatar
        src={FooterVectors}
        alt={''}
        sx={{
          visibility: { xs: 'hidden', lg: 'visible' },
          position: 'absolute',
          width: 'auto',
          height: 'auto',
          bottom: '0',
          left: '0',
        }}
      />
    </AppBar>
  ) : (
    <AppBar
      component={'footer'}
      sx={{
        position: 'relative',
        zIndex: 1100,
      }}
    >
      <Stack
        sx={{
          gap: '32px',
          padding: '32px 24px',
        }}
      >
        <Stack
          direction={'row'}
          sx={{
            gap: '24px',
          }}
        >
          <Stack onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
            <Avatar
              src={MobileFooterLogo}
              alt={''}
              sx={{
                width: '140.21px',
                height: '90.34px',
                objectFit: 'contain',
              }}
            />
          </Stack>
        </Stack>
        <Stack
          direction={'row'}
          sx={{
            gap: '32px',
            flexWrap: 'wrap',
          }}
        >
          {Object.values(dictionary.links).map((link, index1) => (
            <Stack
              component={'ul'}
              key={index1}
              sx={{
                width: 'auto',
                gap: '12px',
                padding: 0,
                margin: 0,
              }}
            >
              <Typography
                variant={'body1'}
                tabIndex={0}
                component={'h5'}
                sx={{
                  color: '#fff',
                }}
              >
                {link.title}
              </Typography>
              {link.redirects.map((redirect, index2) => (
                <ListItem key={index2} sx={{ padding: 0 }}>
                  <Link href={redirect.href} underline={'none'}>
                    <Typography
                      variant={'body6'}
                      sx={{
                        color: '#fff',
                      }}
                    >
                      {redirect.label}
                    </Typography>
                  </Link>
                </ListItem>
              ))}
            </Stack>
          ))}
        </Stack>
        <Divider
          orientation='horizontal'
          sx={{ borderColor: 'primary.shade_500' }}
        />
        <Stack
          direction={'row'}
          sx={{
            gap: '20px',
            height: '36px',
          }}
        >
          <Button
            variant={'primary'}
            role={'link'}
            href={`tel:${mokedNumber}`}
            sx={{
              width: '85px',
              height: '100%',
              padding: '8px 10px',
              gap: '6px',
            }}
          >
            <Avatar
              alt={''}
              src={FooterCall}
              sx={{ width: '20px', height: '20px' }}
            />
            <Typography variant={'body5'}>{dictionary.callButton}</Typography>
          </Button>
          <Button
            variant={'purple'}
            sx={{
              width: '111px',
              height: '100%',
              padding: '8px 10px',
              gap: '6px',
            }}
            onClick={() => {
              window.location.href = `${
                import.meta.env.VITE_APP_MARKETING_SITE_URL
              }ContactUs`;
            }}
          >
            <Avatar
              alt={''}
              src={FooterContact}
              sx={{ width: '20px', height: '18px' }}
            />
            <Typography
              variant={'body5'}
              sx={{
                whiteSpace: 'nowrap',
              }}
            >
              {dictionary.contactButton}
            </Typography>
          </Button>
          <Button
            sx={{ padding: '0', minWidth: '0' }}
            tabIndex={0}
            onClick={() => navigate('/personal-team')}
          >
            <Avatar
              alt={''}
              src={MobileContact}
              sx={{ width: '110px', height: '30px' }}
            />
          </Button>
        </Stack>
        <Stack
          direction={'row'}
          sx={{
            justifyContent: 'center',
            gap: '5px',
          }}
        >
          <Typography variant={'body6'}>{dictionary.rightsSymbol}</Typography>
          <Typography variant={'body6'}>{dictionary.rights}</Typography>
        </Stack>
      </Stack>
      <Avatar
        alt={''}
        src={FooterBackgroundVectors}
        sx={{
          position: 'absolute',
          width: 'auto',
          height: 'auto',
          top: '0',
          left: '0',
        }}
      />
    </AppBar>
  );
};
